export function setLimitedMapEntry<K, V>(
  map: Map<K, V>,
  key: K,
  value: V,
  maxEntries: number
): void {
  if (!map.has(key) && map.size >= maxEntries) {
    const oldestKey = map.keys().next().value;
    if (oldestKey !== undefined) {
      map.delete(oldestKey);
    }
  }

  map.set(key, value);
}

export function setLimitedObjectCacheEntry<T>(
  cache: Record<string, T>,
  key: string,
  value: T,
  maxEntries: number
): void {
  const hasKey = Object.prototype.hasOwnProperty.call(cache, key);
  if (!hasKey) {
    const keys = Object.keys(cache);
    if (keys.length >= maxEntries) {
      delete cache[keys[0]];
    }
  }

  cache[key] = value;
}

export function trimMapToSize<K, V>(map: Map<K, V>, maxEntries: number): void {
  if (maxEntries <= 0) {
    map.clear();
    return;
  }

  while (map.size > maxEntries) {
    const oldestKey = map.keys().next().value;
    if (oldestKey === undefined) {
      break;
    }
    map.delete(oldestKey);
  }
}

type EvictionReason = 'capacity' | 'expired' | 'manual';

interface LimitedCacheEntry<V> {
  value: V;
  lastUsedAt: number;
  expiresAt: number | null;
}

interface CreateLimitedMapCacheOptions<K, V> {
  maxEntries: number;
  ttlMs?: number;
  /** When false, reads do not extend TTL (entries expire on schedule). Default true. */
  refreshTtlOnAccess?: boolean;
  onEvict?: (key: K, value: V, reason: EvictionReason) => void;
}

export interface LimitedMapCache<K, V> {
  get: (key: K) => V | undefined;
  set: (key: K, value: V) => void;
  has: (key: K) => boolean;
  delete: (key: K) => void;
  clear: () => void;
  size: () => number;
  cleanupExpired: () => void;
}

export function createLimitedMapCache<K, V>(
  options: CreateLimitedMapCacheOptions<K, V>
): LimitedMapCache<K, V> {
  const { maxEntries, ttlMs, refreshTtlOnAccess = true, onEvict } = options;
  const entries = new Map<K, LimitedCacheEntry<V>>();

  const now = (): number => Date.now();

  const evict = (key: K, reason: EvictionReason): void => {
    const entry = entries.get(key);
    if (!entry) {
      return;
    }

    entries.delete(key);
    onEvict?.(key, entry.value, reason);
  };

  const createExpiresAt = (timestamp: number): number | null => {
    if (!ttlMs || ttlMs <= 0) {
      return null;
    }

    return timestamp + ttlMs;
  };

  const cleanupExpired = (): void => {
    const currentTime = now();

    entries.forEach((entry, key) => {
      if (entry.expiresAt !== null && entry.expiresAt <= currentTime) {
        evict(key, 'expired');
      }
    });
  };

  const touch = (key: K, entry: LimitedCacheEntry<V>): void => {
    const timestamp = now();
    entries.set(key, {
      ...entry,
      lastUsedAt: timestamp,
      expiresAt: createExpiresAt(timestamp),
    });
  };

  const enforceCapacity = (): void => {
    if (entries.size <= maxEntries) {
      return;
    }

    let oldestKey: K | undefined;
    let oldestTimestamp = Number.POSITIVE_INFINITY;

    entries.forEach((entry, key) => {
      if (entry.lastUsedAt < oldestTimestamp) {
        oldestTimestamp = entry.lastUsedAt;
        oldestKey = key;
      }
    });

    if (oldestKey !== undefined) {
      evict(oldestKey, 'capacity');
    }
  };

  return {
    get: (key: K): V | undefined => {
      cleanupExpired();
      const entry = entries.get(key);
      if (!entry) {
        return undefined;
      }

      if (refreshTtlOnAccess) {
        touch(key, entry);
      }
      return entry.value;
    },
    set: (key: K, value: V): void => {
      cleanupExpired();
      const timestamp = now();

      entries.set(key, {
        value,
        lastUsedAt: timestamp,
        expiresAt: createExpiresAt(timestamp),
      });

      enforceCapacity();
    },
    has: (key: K): boolean => {
      cleanupExpired();
      return entries.has(key);
    },
    delete: (key: K): void => {
      evict(key, 'manual');
    },
    clear: (): void => {
      entries.forEach((_, key) => {
        evict(key, 'manual');
      });
    },
    size: (): number => {
      cleanupExpired();
      return entries.size;
    },
    cleanupExpired,
  };
}